import React from "react";
import { motion } from "framer-motion";

type DashboardVariant = "leads" | "customers" | "sales" | "reports";

const sidebarItems = ["Dashboard", "Leads", "Contacts", "Deals", "Tasks", "Reports"];

export const CrmDashboardMockup = ({ variant = "leads" }: { variant?: DashboardVariant }) => {
    const metrics: Record<DashboardVariant, { label: string; value: string; color: string }[]> = {
        leads: [
            { label: "New Leads", value: "128", color: "#9e00ff" },
            { label: "Qualified", value: "64", color: "#002454" },
            { label: "Converted", value: "32", color: "#22c55e" },
        ],
        customers: [
            { label: "Active", value: "842", color: "#9e00ff" },
            { label: "Returning", value: "312", color: "#002454" },
            { label: "At Risk", value: "18", color: "#f59e0b" },
        ],
        sales: [
            { label: "Pipeline", value: "₹18.4L", color: "#9e00ff" },
            { label: "Won", value: "₹6.2L", color: "#22c55e" },
            { label: "Forecast", value: "₹24.8L", color: "#002454" },
        ],
        reports: [
            { label: "Revenue", value: "+18%", color: "#22c55e" },
            { label: "Conversion", value: "32%", color: "#9e00ff" },
            { label: "Churn", value: "2.1%", color: "#002454" },
        ],
    };

    const rows: Record<DashboardVariant, string[]> = {
        leads: ["Rajesh Kumar — Website", "Priya Menon — WhatsApp", "Arun Sharma — Referral", "Kavya Reddy — IndiaMART"],
        customers: ["Acme Corp — Enterprise", "Bright Edu — Education", "Nova Retail — Retail", "HealthFirst — Healthcare"],
        sales: ["Website Redesign — ₹2.4L", "CRM License — ₹1.8L", "Marketing — ₹95K", "Support Plan — ₹45K"],
        reports: ["Monthly Revenue Report", "Lead Source Analysis", "Team Performance Q1", "Pipeline Forecast"],
    };

    return (
        <div className="crm-mockup crm-mockup--dashboard">
            <div className="crm-mockup__chrome">
                <span /><span /><span />
            </div>
            <div className="crm-mockup__body">
                <aside className="crm-mockup__sidebar">
                    <div className="crm-mockup__logo">HonestiIQ</div>
                    {sidebarItems.map((item, i) => (
                        <div key={item} className={`crm-mockup__nav-item ${i === 1 ? "crm-mockup__nav-item--active" : ""}`}>
                            {item}
                        </div>
                    ))}
                </aside>
                <main className="crm-mockup__main">
                    <div className="crm-mockup__topbar">
                        <span className="crm-mockup__page-title">{variant.charAt(0).toUpperCase() + variant.slice(1)}</span>
                        <div className="crm-mockup__avatar" />
                    </div>
                    <div className="crm-mockup__metrics">
                        {metrics[variant].map((m) => (
                            <div key={m.label} className="crm-mockup__metric" style={{ borderTopColor: m.color }}>
                                <span>{m.label}</span>
                                <strong>{m.value}</strong>
                            </div>
                        ))}
                    </div>
                    <div className="crm-mockup__chart">
                        <div className="crm-mockup__bars">
                            {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                                <div key={i} className="crm-mockup__bar" style={{ height: `${h}%` }} />
                            ))}
                        </div>
                    </div>
                    <div className="crm-mockup__table">
                        {rows[variant].map((row) => (
                            <div key={row} className="crm-mockup__row">
                                <span>{row}</span>
                                <span className="crm-mockup__badge">Active</span>
                            </div>
                        ))}
                    </div>
                </main>
            </div>
        </div>
    );
};

export const CrmFloatingCard = ({
    label,
    value,
    trend,
    className,
    delay = 0,
}: {
    label: string;
    value: string;
    trend: string;
    className?: string;
    delay?: number;
}) => (
    <motion.div
        className={`crm-float-card ${className ?? ""}`}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay }}
    >
        <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4 + delay, repeat: Infinity, ease: "easeInOut" }}
        >
            <span className="crm-float-card__label">{label}</span>
            <strong className="crm-float-card__value">{value}</strong>
            <span className="crm-float-card__trend">{trend}</span>
        </motion.div>
    </motion.div>
);

export const CrmMobileMockup = ({ screenIndex = 0 }: { screenIndex?: number }) => {
    const screens = [
        {
            title: "Leads",
            items: [
                { text: "Rajesh K. — Website", badge: "New", color: "#9e00ff" },
                { text: "Priya M. — WhatsApp", badge: "Hot", color: "#f59e0b" },
                { text: "Arun S. — Referral", badge: "Qualified", color: "#22c55e" },
                { text: "Kavya R. — IndiaMART", badge: "New", color: "#9e00ff" },
            ],
        },
        {
            title: "Customers",
            items: [
                { text: "Acme Corp", badge: "Enterprise", color: "#002454" },
                { text: "Bright Edu", badge: "Active", color: "#22c55e" },
                { text: "Nova Retail", badge: "Active", color: "#22c55e" },
                { text: "HealthFirst", badge: "At Risk", color: "#f59e0b" },
            ],
        },
        {
            title: "Tasks",
            items: [
                { text: "Follow up — Rajesh K.", badge: "Due", color: "#f59e0b" },
                { text: "Send proposal — Priya M.", badge: "Today", color: "#9e00ff" },
                { text: "Demo scheduled — Arun S.", badge: "2pm", color: "#002454" },
                { text: "Review contract — Kavya R.", badge: "Tomorrow", color: "#002454" },
            ],
        },
        {
            title: "Alerts",
            items: [
                { text: "New lead assigned to you", badge: "Now", color: "#9e00ff" },
                { text: "Deal won — ₹2.4L", badge: "1h ago", color: "#22c55e" },
                { text: "Meeting in 1 hour", badge: "Reminder", color: "#f59e0b" },
                { text: "Team update from Priya", badge: "New", color: "#002454" },
            ],
        },
        {
            title: "Team",
            items: [
                { text: "Priya — 12 deals active", badge: "Top", color: "#9e00ff" },
                { text: "Arun — 8 follow-ups due", badge: "Alert", color: "#f59e0b" },
                { text: "Kavya — 94% quota", badge: "Star", color: "#22c55e" },
                { text: "Rajesh — 3 new leads", badge: "New", color: "#002454" },
            ],
        },
    ];
    const screen = screens[screenIndex % screens.length];

    return (
        <div className="crm-mockup crm-mockup--mobile">
            <div className="crm-mockup--mobile__notch" />
            <div className="crm-mockup--mobile__status">
                <span>9:41</span>
                <span className="crm-mockup--mobile__signal">●●●</span>
            </div>
            <div className="crm-mockup--mobile__header">HonestiIQ</div>
            <div className="crm-mockup--mobile__screen-title">{screen.title}</div>
            <div className="crm-mockup--mobile__list">
                {screen.items.map((item) => (
                    <div key={item.text} className="crm-mockup--mobile__item">
                        <span className="crm-mockup--mobile__dot" style={{ background: item.color }} />
                        <span className="crm-mockup--mobile__item-text">{item.text}</span>
                        <span className="crm-mockup--mobile__item-badge" style={{ color: item.color }}>{item.badge}</span>
                    </div>
                ))}
            </div>
            <div className="crm-mockup--mobile__nav">
                {["Leads", "Tasks", "CRM", "Alerts"].map((n) => (
                    <span key={n} className={n === screen.title || (n === "CRM" && screen.title === "Customers") || (n === "Alerts" && screen.title === "Team") ? "active" : ""}>
                        {n}
                    </span>
                ))}
            </div>
        </div>
    );
};

export const CrmChartPanel = () => (
    <div className="crm-chart-panel">
        <div className="crm-chart-panel__header">
            <span>Revenue Overview</span>
            <span className="crm-chart-panel__badge">+18%</span>
        </div>
        <div className="crm-chart-panel__line">
            <svg viewBox="0 0 300 80" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="crmLineGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#9e00ff" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#9e00ff" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d="M0,60 L40,50 L80,55 L120,35 L160,40 L200,20 L240,25 L280,10 L300,15 L300,80 L0,80 Z" fill="url(#crmLineGrad)" />
                <path d="M0,60 L40,50 L80,55 L120,35 L160,40 L200,20 L240,25 L280,10 L300,15" fill="none" stroke="#9e00ff" strokeWidth="2.5" />
            </svg>
        </div>
        <div className="crm-chart-panel__donut-row">
            <div className="crm-chart-panel__donut">
                <svg viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e8e8e8" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#9e00ff" strokeWidth="3" strokeDasharray="72 100" strokeLinecap="round" transform="rotate(-90 18 18)" />
                </svg>
                <span>72%</span>
            </div>
            <div className="crm-chart-panel__legend">
                <div><i style={{ background: "#9e00ff" }} /> Converted</div>
                <div><i style={{ background: "#002454" }} /> In Progress</div>
                <div><i style={{ background: "#e8e8e8" }} /> Lost</div>
            </div>
        </div>
    </div>
);

export const CrmWhatsAppMockup = () => (
    <div className="crm-mockup crm-mockup--whatsapp">
        <div className="crm-mockup--whatsapp__header">
            <span>WhatsApp Team Inbox</span>
            <span className="crm-mockup--whatsapp__count">12 active</span>
        </div>
        <div className="crm-mockup--whatsapp__chat">
            <div className="crm-mockup--whatsapp__msg crm-mockup--whatsapp__msg--in">
                Hi, I&apos;m interested in your CRM solution.
            </div>
            <div className="crm-mockup--whatsapp__msg crm-mockup--whatsapp__msg--out">
                Hello! I&apos;d be happy to help. Let me share details.
            </div>
            <div className="crm-mockup--whatsapp__msg crm-mockup--whatsapp__msg--in">
                Can we schedule a demo this week?
            </div>
            <div className="crm-mockup--whatsapp__tag">Lead: Qualified ✓</div>
        </div>
    </div>
);

export const CrmContactProfileMockup = () => (
    <div className="crm-profile-mock">
        <div className="crm-profile-mock__header">
            <div className="crm-profile-mock__avatar">RK</div>
            <div>
                <strong>Rajesh Kumar</strong>
                <span>Acme Solutions Pvt Ltd</span>
            </div>
            <span className="crm-profile-mock__tag">Hot Lead</span>
        </div>
        <div className="crm-profile-mock__grid">
            <div><label>Email</label><span>rajesh@acme.in</span></div>
            <div><label>Phone</label><span>+91 98765 43210</span></div>
            <div><label>Source</label><span>WhatsApp</span></div>
            <div><label>Stage</label><span>Qualified</span></div>
        </div>
        <div className="crm-profile-mock__timeline">
            <div className="crm-profile-mock__event"><span>Today</span> WhatsApp inquiry received</div>
            <div className="crm-profile-mock__event"><span>Yesterday</span> Demo call scheduled</div>
            <div className="crm-profile-mock__event"><span>2 days ago</span> Lead assigned to Priya</div>
            <div className="crm-profile-mock__event"><span>3 days ago</span> Lead captured from website</div>
        </div>
    </div>
);

export const CrmWorkflowVisual = () => (
    <div className="crm-flow-visual">
        {["Lead Received", "Auto Assign", "Task Created", "Follow-Up Sent"].map((step, i, arr) => (
            <React.Fragment key={step}>
                <div className="crm-flow-visual__node">
                    <span>{i + 1}</span>
                    <p>{step}</p>
                </div>
                {i < arr.length - 1 && <div className="crm-flow-visual__arrow">→</div>}
            </React.Fragment>
        ))}
    </div>
);

export const CrmCommandCenterMockup = () => (
    <div className="crm-mockup crm-mockup--command">
        <div className="crm-mockup__chrome">
            <span /><span /><span />
            <span className="crm-mockup__chrome-title">CRM Dashboard — Command Center</span>
        </div>
        <div className="crm-mockup__body">
            <aside className="crm-mockup__sidebar">
                <div className="crm-mockup__logo">HonestiIQ</div>
                {["Dashboard", "Leads", "Pipeline", "Tasks", "Reports", "Team"].map((item, i) => (
                    <div key={item} className={`crm-mockup__nav-item ${i === 0 ? "crm-mockup__nav-item--active" : ""}`}>
                        {item}
                    </div>
                ))}
            </aside>
            <main className="crm-mockup__main crm-mockup__main--command">
                <div className="crm-mockup__topbar">
                    <span className="crm-mockup__page-title">Command Center</span>
                    <div className="crm-mockup__topbar-actions">
                        <span className="crm-mockup__pill">12 Follow-Ups</span>
                        <div className="crm-mockup__avatar" />
                    </div>
                </div>
                <div className="crm-mockup__metrics crm-mockup__metrics--4">
                    {[
                        { label: "Active Leads", value: "248", color: "#9e00ff" },
                        { label: "Pipeline Value", value: "₹2.4M", color: "#002454" },
                        { label: "Revenue (MTD)", value: "₹6.8L", color: "#22c55e" },
                        { label: "Team Score", value: "94%", color: "#f59e0b" },
                    ].map((m) => (
                        <div key={m.label} className="crm-mockup__metric" style={{ borderTopColor: m.color }}>
                            <span>{m.label}</span>
                            <strong>{m.value}</strong>
                        </div>
                    ))}
                </div>
                <div className="crm-mockup__command-grid">
                    <div className="crm-mockup__chart crm-mockup__chart--tall">
                        <span className="crm-mockup__chart-label">Revenue Tracking</span>
                        <div className="crm-mockup__bars">
                            {[35, 55, 42, 70, 58, 85, 72, 95].map((h, i) => (
                                <div key={i} className="crm-mockup__bar" style={{ height: `${h}%` }} />
                            ))}
                        </div>
                    </div>
                    <div className="crm-mockup__activity">
                        <span className="crm-mockup__chart-label">Recent Activities</span>
                        {[
                            "Rajesh K. moved to Qualified",
                            "Priya closed deal — ₹2.4L",
                            "Follow-up sent to Arun S.",
                            "New lead from WhatsApp",
                        ].map((a) => (
                            <div key={a} className="crm-mockup__activity-row">
                                <span className="crm-mockup__activity-dot" />
                                {a}
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    </div>
);

export const CrmPipelineDashboard = () => (
    <div className="crm-mockup crm-mockup--pipeline-dash">
        <div className="crm-mockup__chrome">
            <span /><span /><span />
            {/* <span className="crm-mockup__chrome-title">CRM Features — Sales Pipeline</span> */}
        </div>
        <div className="crm-mockup__pipeline-body">
            <div className="crm-mockup__pipeline-header">
                <span>Pipeline Overview</span>
                <span className="crm-mockup__pill">₹2.4M Total</span>
            </div>
            <div className="crm-mockup__pipeline-bars">
                {[
                    { label: "New Leads", count: 42, pct: 100 },
                    { label: "Contacted", count: 28, pct: 67 },
                    { label: "Qualified", count: 17, pct: 40 },
                    { label: "Proposal", count: 11, pct: 26 },
                    { label: "Negotiation", count: 6, pct: 14 },
                    { label: "Won", count: 4, pct: 10 },
                ].map((s) => (
                    <div key={s.label} className="crm-mockup__pipeline-row">
                        <span className="crm-mockup__pipeline-label">{s.label}</span>
                        <div className="crm-mockup__pipeline-track">
                            <div className="crm-mockup__pipeline-fill" style={{ width: `${s.pct}%` }} />
                        </div>
                        <span className="crm-mockup__pipeline-count">{s.count}</span>
                    </div>
                ))}
            </div>
            <div className="crm-mockup__pipeline-footer">
                <div><span>Conversion</span><strong>32%</strong></div>
                <div><span>Forecast</span><strong>₹18.6L</strong></div>
                <div><span>Monthly</span><strong>+24%</strong></div>
            </div>
        </div>
    </div>
);

export const CrmAnalyticsDashboard = () => (
    <div className="crm-mockup crm-mockup--analytics">
        <div className="crm-mockup__chrome">
            <span /><span /><span />
            <span className="crm-mockup__chrome-title">CRM Analytics — Insights</span>
        </div>
        <div className="crm-mockup__analytics-body">
            <div className="crm-mockup__analytics-top">
                {[
                    { label: "Revenue", value: "₹24.8L", change: "+18%" },
                    { label: "Sales Performance", value: "87%", change: "+12%" },
                    { label: "Lead Sources", value: "6", change: "channels" },
                    { label: "Conversion", value: "32.4%", change: "+5.2%" },
                ].map((m) => (
                    <div key={m.label} className="crm-mockup__analytics-metric">
                        <span>{m.label}</span>
                        <strong>{m.value}</strong>
                        <em>{m.change}</em>
                    </div>
                ))}
            </div>
            <div className="crm-mockup__analytics-charts">
                <div className="crm-mockup__analytics-chart">
                    <span>Revenue Trends</span>
                    <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="crmAnalyticsGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#9e00ff" stopOpacity="0.35" />
                                <stop offset="100%" stopColor="#9e00ff" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path d="M0,45 L25,38 L50,42 L75,28 L100,32 L125,18 L150,22 L175,10 L200,14 L200,60 L0,60 Z" fill="url(#crmAnalyticsGrad)" />
                        <path d="M0,45 L25,38 L50,42 L75,28 L100,32 L125,18 L150,22 L175,10 L200,14" fill="none" stroke="#9e00ff" strokeWidth="2" />
                    </svg>
                </div>
                <div className="crm-mockup__analytics-bars">
                    <span>Team Productivity</span>
                    {[72, 88, 65, 94, 78, 91].map((h, i) => (
                        <div key={i} className="crm-mockup__analytics-bar" style={{ height: `${h}%` }} />
                    ))}
                </div>
            </div>
        </div>
    </div>
);
